import { CommonModule } from '@angular/common';
import { Component, computed } from '@angular/core';
import { ImageZoomDirective } from '@app/components/zoom/image-zoom-directive/image-zoom.directive';
import { XMLElementBaseComponent } from '../base';
import { environment } from '@env';

@Component({
  selector: 'app-xml-tool-image',
  imports: [ImageZoomDirective, CommonModule],
  standalone: true,
  template: `
    <div class="my-2">
      <button
        class="flex w-full cursor-pointer gap-3 rounded border border-gray-200 bg-white p-2 text-left hover:bg-gray-50"
        tabindex="0"
        [attr.aria-expanded]="isExpanded"
        (click)="toggleExpanded()"
        (keydown)="onKeyDown($event)">
        <!-- Small preview image or error placeholder -->
        @if (!thumbnailError) {
          <img
            [src]="thumbnailSrc()"
            [alt]="'Tool: ' + data.name"
            class="h-12 w-12 flex-shrink-0 rounded border border-gray-300 object-cover"
            (error)="onThumbnailError($event)" />
        } @else {
          <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded border border-red-300 bg-red-100">
            <svg class="h-6 w-6 text-red-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path
                fill-rule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clip-rule="evenodd" />
            </svg>
          </div>
        }

        <!-- Tool info -->
        <div class="flex-1">
          <div class="text-sm font-medium text-gray-800">{{ data.name }}</div>
          <div class="text-xs text-gray-500">
            @if (thumbnailError) {
              Failed to load image
            }
          </div>
        </div>

        <!-- Expand/collapse icon -->
        @if (!thumbnailError) {
          <svg
            class="h-4 w-4 text-gray-400 transition-transform duration-200"
            [class.rotate-180]="isExpanded"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        }
      </button>

      <!-- Expanded full-size image -->
      <div [ngClass]="{ hidden: !(isExpanded && !thumbnailError) }" class="print:block">
        <div class="mt-3 text-center" appImageZoom>
          @if (!fullImageError) {
            <img
              [src]="imageSrc()"
              [alt]="'Tool: ' + data.name"
              class="h-auto max-w-full rounded-lg border border-gray-300 shadow-md"
              (error)="onFullImageError($event)" />
          } @else {
            <div class="flex items-center justify-center rounded-lg border border-red-300 bg-red-50 p-8">
              <div class="text-center">
                <svg class="mx-auto h-12 w-12 text-red-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fill-rule="evenodd"
                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                    clip-rule="evenodd" />
                </svg>
                <div class="mt-2 text-sm text-red-600">Failed to load full-size image</div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- <div class="bg-violet-100 text-xs">{{ data | json }}</div> -->
  `,
})
export class ToolImageComponent extends XMLElementBaseComponent {
  thumbnailError = false;
  fullImageError = false;
  isExpanded = false;

  imageSrc = computed(() => {
    const apiUrl = environment.api.url ?? '';
    const provider = this.provider === 'volkswagen' ? 'vw' : this.provider;
    const vehicleId = this.data.vehicleId;
    const imageId = this.data.name;
    return `${apiUrl}/rmi/${provider}/proxy-image?vehicleId=${vehicleId}&imageId=${imageId}`;
  });

  thumbnailSrc = computed(() => {
    const apiUrl = environment.api.url ?? '';
    const provider = this.provider === 'volkswagen' ? 'vw' : this.provider;
    const vehicleId = this.data.vehicleId;
    const imageId = this.data.name;
    return `${apiUrl}/rmi/${provider}/proxy-image?subject=RL&vehicleId=${vehicleId}&imageId=${imageId}`;
  });

  onThumbnailError(event: any) {
    void event;
    this.thumbnailError = true;
  }

  onFullImageError(event: any) {
    void event;
    this.fullImageError = true;
  }

  toggleExpanded() {
    if (!this.thumbnailError) {
      this.isExpanded = !this.isExpanded;
      // Reset full image error when toggling
      if (this.isExpanded) {
        this.fullImageError = false;
      }
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleExpanded();
    }
  }
}
